import React, { useImperativeHandle, useRef, useState } from 'react';
import { Button, Col, Form, Row, Spin, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import moment from 'moment';
import intl from 'react-intl-universal';
import CustomDrawer, { IdrawerRef } from '@/components/CustomDrawer';
import { getMobileByOrderNo, getOrderDetail } from '@/api/crsReservation';
import { DATE_FORMAT, DATE_TIME_FORMAT, OCC_TYPE_MAP } from '@/config';
import { uuid } from '@/utils';
import { parseFen2Currency } from '@/utils/number';
import { getTimeByUtcOffset } from '@/utils/intl';
import { formatAreaMobileStr } from '@/components/BaseMobileInput/helper';
import styles from './index.module.scss';

enum DouyinStatus {
  NOT_PAY = 0, // "待支付"
  WAIT_RECEIVING_ORDER = 1, // "待接单"
  RECEIVING_ORDER = 2, // "接单中"
  RECEIVED_ORDER = 3, // "已接单"
  APPLY_CANCELING = 4, // "取消申请中"
  CANCELING = 5, // "取消中"
  CANCEL = 6, // "已取消"
  CHECK_IN = 7, // "已入住"
  SETTLED = 8, // "已结账"
}
const STATUS_MAP = {
  [DouyinStatus.NOT_PAY]: intl.get('config_constant_channel_order_status_1'),
  [DouyinStatus.RECEIVED_ORDER]: intl.get('config_constant_channel_order_status_51'),
  [DouyinStatus.CHECK_IN]: intl.get('config_constant_channel_order_status_14'),
  [DouyinStatus.SETTLED]: intl.get('config_constant_channel_order_status_2'),
  [DouyinStatus.CANCEL]: intl.get('config_constant_channel_order_status_5'),

  [DouyinStatus.WAIT_RECEIVING_ORDER]: intl.get('channel_WAIT_RECEIVING_ORDER'),
  [DouyinStatus.RECEIVING_ORDER]: intl.get('channel_RECEIVING_ORDER'),
  [DouyinStatus.APPLY_CANCELING]: intl.get('channel_APPLY_CANCELING'),
  [DouyinStatus.CANCELING]: intl.get('channel_CANCELING'),
};

const DouyinChannelDetailDrawer = React.forwardRef((props, ref) => {
  const [detail, setDetail] = useState<any>({});
  const [loading, setLoading] = useState<boolean>(false);

  // 明文手机号
  const [mobile, setMobile] = useState('');
  const [mobileLoading, setMobileLoading] = useState(false);

  const drawerRef = useRef<IdrawerRef>();
  const paramsRef = useRef<any>();

  const handleToggleMobile = async () => {
    if (mobile) {
      setMobile('');
    } else {
      const { orderNo } = paramsRef.current || {};
      if (orderNo) {
        try {
          setMobileLoading(true);
          const res = await getMobileByOrderNo({ type: 1, content: orderNo });
          if (res.code === 0) {
            setMobile(res.data?.result);
          }
          setMobileLoading(false);
        } catch (error) {
          setMobileLoading(false);
        }
      }
    }
  };

  const handleDrawerClose = () => {
    drawerRef.current.closeDrawer();
  };

  useImperativeHandle(
    ref,
    () => ({
      async openDrawer({ orderNo }) {
        paramsRef.current = { orderNo };
        setMobile('');
        setMobileLoading(false);
        drawerRef.current.openDrawer();
        try {
          setLoading(true);
          const res = await getOrderDetail(orderNo);
          if (res.code === 0) {
            const channelDetail = res?.data || {};
            // 日期分组下房间下标
            let currentDateStr = null;
            let roomIndex = 0;
            // 总房间数
            let totalRoomNo = 0;
            // 总房间标价
            let totalRoomBasePrice = 0;
            // 总促销金额
            let totalCouponAmount = 0;
            // 总佣金
            let totalCommission = 0;
            // 总执行房价
            let totalExecutePrice = 0;
            channelDetail.items = channelDetail.items
              ?.sort((pre, next) => {
                if (pre.businessDate < next.businessDate) return -1;
                if (pre.businessDate > next.businessDate) return 1;
                return 0;
              })
              ?.map((item) => {
                const tempItem = { ...item };
                tempItem.id = uuid();
                const dateStr = getTimeByUtcOffset(tempItem.businessDate, DATE_FORMAT);
                if (currentDateStr !== dateStr) {
                  currentDateStr = dateStr;
                  roomIndex = 1;
                  tempItem.roomNoIndex = roomIndex;
                } else {
                  roomIndex += 1;
                  tempItem.roomNoIndex = roomIndex;
                }
                totalRoomNo += 1;
                totalRoomBasePrice += tempItem.roomBasePrice;
                totalCouponAmount += tempItem.couponAmount;
                totalCommission += tempItem.commission;
                totalExecutePrice += tempItem.executePrice;
                return tempItem;
              });
            setDetail(
              Object.assign(channelDetail, {
                totalRoomNo,
                totalRoomBasePrice,
                totalCouponAmount,
                totalCommission,
                totalExecutePrice,
              }),
            );
          }
          setLoading(false);
        } catch (error) {
          setLoading(false);
        }
      },
    }),
    [],
  );

  const columns: ColumnsType<any> = [
    {
      title: intl.get('channel_business_date'),
      dataIndex: 'businessDate',
      render: (val) => (val ? getTimeByUtcOffset(val, DATE_FORMAT) : ''),
    },
    {
      title: intl.get('app_room'),
      dataIndex: 'roomNoIndex',
      render: (val) => intl.get('channel_room_what', { val }),
    },
    {
      title: intl.get('channel_room_price'),
      dataIndex: 'roomBasePrice',
      render: (val) => parseFen2Currency(val),
      align: 'right',
    },
    { title: intl.get('channel_promotion_type'), render: () => '-', align: 'right' },
    {
      title: intl.get('channel_promotion_amount'),
      dataIndex: 'couponAmount',
      render: (val) => parseFen2Currency(val),
      align: 'right',
    },
    {
      title: intl.get('channel_estimated_commission_service_fee'),
      dataIndex: 'commission',
      render: (val) => parseFen2Currency(val),
      align: 'right',
    },
    {
      title: intl.get('channel_execute_room_price'),
      dataIndex: 'executePrice',
      render: (val) => parseFen2Currency(val),
      align: 'right',
    },
  ];

  return (
    <>
      <CustomDrawer
        ref={drawerRef}
        title={intl.get('channel_tiktok_direct_channel_order_detail')}
        onClose={handleDrawerClose}
        footer={<Button onClick={handleDrawerClose}>{intl.get('app_btn_close')}</Button>}
      >
        <Spin spinning={loading}>
          <div className={styles.container}>
            <div className="space-between" style={{ marginBottom: 16 }}>
              <div className="groupLabel">{intl.get('channel_order_info')}</div>
            </div>
            <Row>
              <Col span={8}>
                <Form.Item label={intl.get('app_reservation_channel_no')}>
                  {detail?.orderNo}
                </Form.Item>
                <Form.Item label={intl.get('channel_make_order_room_type')}>
                  {detail?.roomTypeName}
                </Form.Item>
                <Form.Item label={intl.get('app_reservation_eta_time')}>
                  {detail?.expectedArrivalDate
                    ? getTimeByUtcOffset(detail?.expectedArrivalDate, DATE_TIME_FORMAT)
                    : ''}
                </Form.Item>
                <Form.Item label={intl.get('app_constants_booker_name')}>{detail?.name}</Form.Item>
                <Form.Item label={intl.get('channel_cancel_rule')}>
                  {detail?.douyinCancelRules}
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label={intl.get('drawer_crs_reserve_channel_time_label')}>
                  {detail?.createTime
                    ? getTimeByUtcOffset(detail?.createTime, DATE_TIME_FORMAT)
                    : ''}
                </Form.Item>
                <Form.Item label={intl.get('channel_booking_room_num')}>
                  {detail?.roomNum}
                </Form.Item>
                <Form.Item label={intl.get('app_reservation_etd_time')}>
                  {detail?.expectedDepartureDate
                    ? getTimeByUtcOffset(detail?.expectedDepartureDate, DATE_TIME_FORMAT)
                    : ''}
                </Form.Item>
                <Form.Item label={intl.get('app_reservation_phone_number')}>
                  <span style={{ minWidth: 100, display: 'inline-block' }}>
                    {formatAreaMobileStr(mobile || detail?.phoneNo)}
                  </span>
                  <Button
                    type="ghost"
                    loading={mobileLoading}
                    style={{ marginLeft: 8 }}
                    onClick={handleToggleMobile}
                  >
                    {mobile ? intl.get('channel_hide') : intl.get('app_look')}
                  </Button>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label={intl.get('app_reservation_order_status')}>
                  {STATUS_MAP[detail?.status]}
                </Form.Item>
                <Form.Item label={intl.get('app_reservation_occ_type')}>
                  {OCC_TYPE_MAP[detail?.occType]}
                </Form.Item>
                <Form.Item label={intl.get('app_reservation_occ_duration')}>
                  {detail?.stayLength
                    ? intl.get('channel_many_nights', { val: detail?.stayLength })
                    : ''}
                </Form.Item>
              </Col>
            </Row>

            <div className="space-between" style={{ marginBottom: 16, marginTop: 32 }}>
              <div className="groupLabel">{intl.get('channel_room_fee_info')}</div>
            </div>
            <Row>
              <Col span={8}>
                <Form.Item label={intl.get('channel_user_actual_pay')}>
                  {parseFen2Currency(detail?.crsAmount)}
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label={intl.get('channel_pay_time')}>
                  {detail?.status !== DouyinStatus.NOT_PAY && detail?.orderTime
                    ? getTimeByUtcOffset(detail?.orderTime, DATE_TIME_FORMAT)
                    : '-'}
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label={intl.get('app_reservation_strategy_name')}>
                  {detail?.priceStrategyName || '-'}
                </Form.Item>
              </Col>
            </Row>
            <Table
              rowKey="id"
              columns={columns}
              dataSource={detail?.items}
              pagination={false}
              scroll={{ x: 'max-content' }}
              summary={() => (
                <Table.Summary fixed>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0}>
                      {intl.get('drawer_crs_reserve_room_summary')}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1}>{detail?.totalRoomNo}</Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right">
                      {parseFen2Currency(detail?.totalRoomBasePrice)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align="right">
                      -
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4} align="right">
                      {parseFen2Currency(detail?.totalCouponAmount)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={5} align="right">
                      {parseFen2Currency(detail?.totalCommission)}
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={6} align="right">
                      {parseFen2Currency(detail?.totalExecutePrice)}
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              )}
            />

            {detail?.status === DouyinStatus.CANCEL ? (
              <>
                <div className="space-between" style={{ marginBottom: 16, marginTop: 32 }}>
                  <div className="groupLabel">{intl.get('channel_cancel_info')}</div>
                </div>
                <Form.Item label={intl.get('channel_cancel_type')}>{detail?.reason}</Form.Item>
              </>
            ) : null}
          </div>
        </Spin>
      </CustomDrawer>
    </>
  );
});

export default DouyinChannelDetailDrawer;
